import React from "react";
import FadeIn from "react-fade-in/lib/FadeIn";
import { Row, Col } from "react-flexbox-grid";
import {
  Nav,
  Card,
  Loader,
  BarChart,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "ui-kit-ck-consultant";

import ShowOptionsNGRA from "../../../components/general/ShowOptionsNGRA-v2";

import axiosConfig from "../../../utils/axiosConfig";
import handleSortClick from "../../../utils/handleSortClick";
import filterStore from "../../../stores/filterStore";
import { getPercentageValue } from "../../../utils/general";

import { colors } from "../../../utils/colors";

const EMPTY_DATA = {
  total: null,
  months: [],
  agencies: [],
  documents: [],
};

const AGENCY_COLUMNS = [
  { key: "agencyName", label: "Agence" },
  { key: "regionName", label: "Région" },
  { key: "folders", label: "Dossiers" },
  { key: "sentClient", label: "Dossiers notifiés assuré" },
  { key: "readClient", label: "Consultés assuré" },
  { key: "rateClient", label: "Taux assuré" },
  { key: "sentGarage", label: "Dossiers notifiés réparateur" },
  { key: "readGarage", label: "Consultés réparateur" },
  { key: "rateGarage", label: "Taux réparateur" },
];

const DOCUMENT_COLUMNS = [
  { key: "type", label: "Type de document" },
  { key: "sentClient", label: "Envoyés assuré" },
  { key: "readClient", label: "Lus assuré" },
  { key: "rateClient", label: "Taux assuré" },
  { key: "sentGarage", label: "Envoyés réparateur" },
  { key: "readGarage", label: "Lus réparateur" },
  { key: "rateGarage", label: "Taux réparateur" },
];

const getRate = (value) =>
  value === null || value === undefined ? "-" : getPercentageValue(value);

const getCount = (value) => new Intl.NumberFormat("fr-FR").format(value || 0);

export default class Consultation extends React.Component {
  constructor(props) {
    super(props);
    const saved = filterStore.getState().getPageState("consultation");
    this.state = saved || {
      isLoad: false,
      network: 0,
      group: 0,
      region: 0,
      agency: 0,
      expert: 0,
      company: 0,
      dateStart: null,
      dateEnd: null,
      agencyOrder: { name: "agencyName", desc: false },
      documentOrder: { name: "sentClient", desc: true },
      ...EMPTY_DATA,
    };

    this.agencySortRef = AGENCY_COLUMNS.map(() => React.createRef());
    this.documentSortRef = DOCUMENT_COLUMNS.map(() => React.createRef());
  }

  componentWillUnmount() {
    filterStore.getState().setPageState("consultation", this.state);
  }

  handleAgencySortClick = (desc, column) => {
    this.setState({
      agencyOrder: handleSortClick(this.agencySortRef, desc, column),
    });
  };

  handleDocumentSortClick = (desc, column) => {
    this.setState({
      documentOrder: handleSortClick(this.documentSortRef, desc, column),
    });
  };

  handleDataChange = (data) => {
    filterStore.getState().setPageFilters("consultation", data);
    this.setState(
      {
        network: data.network,
        group: data.group,
        region: data.region,
        agency: data.agency,
        expert: data.user,
        company: data.company,
        dateStart: data.dateStart,
        dateEnd: data.dateEnd,
      },
      () => {
        this.getData();
      }
    );
  };

  getData = () => {
    this.setState({ isLoad: true }, () => {
      axiosConfig("quality/consultation/get-data", {
        data: {
          network: this.state.network,
          group: this.state.group,
          region: this.state.region,
          agency: this.state.agency,
          expert: this.state.expert,
          company: this.state.company,
          dateStart: this.state.dateStart,
          dateEnd: this.state.dateEnd,
        },
      })
        .then((res) => {
          if (res.data.success) {
            this.setState({
              isLoad: false,
              total: res.data.total,
              months: res.data.months,
              agencies: res.data.agencies,
              documents: res.data.documents,
            });
          } else {
            this.setState({ isLoad: false, ...EMPTY_DATA }, () =>
              alert("Une erreur est survenue")
            );
          }
        })
        .catch(() =>
          this.setState({ isLoad: false, ...EMPTY_DATA }, () =>
            alert("Une erreur est survenue")
          )
        );
    });
  };

  getSortedRows = (rows, order) =>
    [...rows].sort((a, b) => {
      const left = a[order.name];
      const right = b[order.name];

      if (typeof left === "string" || typeof right === "string") {
        return order.desc
          ? (right || "").localeCompare(left || "")
          : (left || "").localeCompare(right || "");
      }

      // A rate is null when nothing was sent to that audience: keep those rows
      // at the bottom whichever way the column is sorted.
      const leftValue = left === null || left === undefined ? -1 : left;
      const rightValue = right === null || right === undefined ? -1 : right;

      return order.desc ? rightValue - leftValue : leftValue - rightValue;
    });

  renderCard = (title, read, sent, rate) => (
    <Col xs={12} md={6} lg={3}>
      <Card className="mb-10 mb-20-xl" title={title}>
        <h2 className="mb-0">{getRate(rate)}</h2>
        <span className="gray">
          {`${getCount(read)} / ${getCount(sent)}`}
        </span>
      </Card>
    </Col>
  );

  renderMonths() {
    const { months } = this.state;

    return (
      <Card title="Évolution mensuelle" className="mb-10 mb-30-xl">
        <BarChart
          isLegend
          title=""
          height={400}
          digits={1}
          labels={months.map((month) => month.month)}
          dataSet={[
            {
              label: "Taux de consultation assuré",
              type: "line",
              data: months.map((month) => month.rateClient),
              color: colors.blue[0],
              yAxisID: "A",
            },
            {
              label: "Taux de consultation réparateur",
              type: "line",
              data: months.map((month) => month.rateGarage),
              color: colors.red[0],
              yAxisID: "A",
            },
            {
              label: "Dossiers notifiés assuré",
              type: "bar",
              data: months.map((month) => month.sentClient),
              color: colors.blue[1],
              yAxisID: "B",
            },
            {
              label: "Dossiers notifiés réparateur",
              type: "bar",
              data: months.map((month) => month.sentGarage),
              color: colors.red[1],
              yAxisID: "B",
            },
          ]}
          yAxes={[
            { id: "A", ticks: { min: 0, beginAtZero: true } },
            { id: "B", ticks: { min: 0, beginAtZero: true } },
          ]}
        />
      </Card>
    );
  }

  renderTable(title, columns, rows, order, onSort, sortRef) {
    return (
      <Card
        title={title}
        className="mb-10 mb-30-xl"
        classNameChildren="overflow-x-auto"
      >
        <Table isCSV>
          <Thead>
            <Tr>
              <Th>#</Th>
              {columns.map((column, idx) => (
                <Th
                  key={column.key}
                  onSort={(desc) => onSort(desc, idx)}
                  ref={sortRef[idx]}
                  sort
                  nameSort={column.key}
                >
                  {column.label}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {this.getSortedRows(rows, order).map((row, idx) => (
              <Tr key={row.agencyId || row.type}>
                <Th>{idx + 1}</Th>
                {columns.map((column) => (
                  <Td key={column.key}>
                    {column.key.startsWith("rate")
                      ? getRate(row[column.key])
                      : typeof row[column.key] === "number"
                        ? getCount(row[column.key])
                        : row[column.key]}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Card>
    );
  }

  render() {
    const { total, months, agencies, documents } = this.state;

    return (
      <>
        <FadeIn>
          <Nav title="Consultation ixperience" className="mb-10 mb-20-xl" />
          <ShowOptionsNGRA
            isChange={true}
            className="mb-10 mb-20-xl"
            onDataChange={this.handleDataChange}
            initialFilters={filterStore
              .getState()
              .getPageFilters("consultation")}
            visibilityRole={this.props.visibilityRole}
            visibilityType={this.props.visibilityType}
            preloadRole={this.props.preloadRole}
            preloadType={this.props.preloadType}
            limitUserType={this.props.limitUserType}
            limitUserRole={this.props.limitUserRole}
            isCompany={true}
            isUser={true}
            isDateStart={true}
            isDateEnd={true}
            isAll={true}
          />
          {total ? (
            <>
              <Row>
                {this.renderCard(
                  "Dossiers consultés par l'assuré",
                  total.readClient,
                  total.sentClient,
                  total.rateClient
                )}
                {this.renderCard(
                  "Dossiers consultés par le réparateur",
                  total.readGarage,
                  total.sentGarage,
                  total.rateGarage
                )}
                <Col xs={12} md={6} lg={3}>
                  <Card
                    className="mb-10 mb-20-xl"
                    title="Dossiers avec notification"
                  >
                    <h2 className="mb-0">{getCount(total.folders)}</h2>
                    <span className="gray">
                      sur la période sélectionnée
                    </span>
                  </Card>
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <Card
                    className="mb-10 mb-20-xl"
                    title="Types de document envoyés"
                  >
                    <h2 className="mb-0">{getCount(documents.length)}</h2>
                    <span className="gray">
                      dont le taux de lecture est détaillé ci-dessous
                    </span>
                  </Card>
                </Col>
              </Row>
              <p className="gray mb-10 mb-20-xl">
                Un dossier est consulté dès qu'un message qui lui était adressé
                a été ouvert dans l'espace ixperience, ou que le lien de l'email
                de notification a été cliqué. Le taux de lecture par type de
                document, lui, ne compte que les ouvertures de message : un clic
                sur l'email prouve l'accès au dossier, pas la lecture d'un
                document précis.
              </p>
              {months.length > 0 && this.renderMonths()}
              {documents.length > 0 &&
                this.renderTable(
                  "Taux de lecture par type de document",
                  DOCUMENT_COLUMNS,
                  documents,
                  this.state.documentOrder,
                  this.handleDocumentSortClick,
                  this.documentSortRef
                )}
              {agencies.length > 0 &&
                this.renderTable(
                  "Consultation par agence",
                  AGENCY_COLUMNS,
                  agencies,
                  this.state.agencyOrder,
                  this.handleAgencySortClick,
                  this.agencySortRef
                )}
            </>
          ) : (
            <Card>
              <p>Aucune donnée n'est disponible</p>
            </Card>
          )}
        </FadeIn>
        <Loader show={this.state.isLoad} />
      </>
    );
  }
}
